import { createSelector } from "@reduxjs/toolkit";
import {
  DEVICES_TYPES_SETTINGS,
  type IMLPredictDevice,
} from "@multiconsult-group/multiplacer-core";
import type { IPixPath, IPixPolygon } from "@multiconsult-group/multiplacer-ui";
import { buildCeilingGridPixPathsFromLines } from "../../buildCeilingGridPixPathsFromLines";
import { convertMcBimSpaceToPixPolygon } from "../../DesignTab/pixpolygonSelectors";
import { selectActivePartialMcbimSpace } from "../../McBim/mcbimSelectors";
import { selectHttpSingleRoomFull } from "../../Room/httpRoomSelectors";
import { getSqaureAroundCoordinate } from "../../utils";

const createAiPredictionPolygon = (
  prediction: IMLPredictDevice,
  index: number
): IPixPolygon | null => {
  if (!prediction.coordinate || prediction.coordinate.length < 2) {
    return null;
  }

  const color = DEVICES_TYPES_SETTINGS[prediction.deviceCategory]?.color ?? "#2563eb";

  return {
    id: `ai-ceiling-prediction-${index}-${prediction.deviceCategory}`,
    points: getSqaureAroundCoordinate(prediction.coordinate, prediction.direction),
    fillColor: color,
    strokeColor: "#0ea5e9",
    strokeWidth: "0.08",
    text: {
      show: false,
      values: [String(index + 1)],
      color: "#000000",
      fontSize: "1",
    },
    tooltip: prediction.deviceCategory,
    isMoveable: false,
    isDragSelectable: false,
  };
};

export const selectCeilingAiPredictionsRoomPixPolygon = createSelector(
  [selectActivePartialMcbimSpace],
  (mcbimSpace): IPixPolygon | undefined => {
    const polygon = convertMcBimSpaceToPixPolygon({
      mcBimSpace: mcbimSpace ?? undefined,
      dragSelectSpaces: false,
      showText: false,
    });

    if (!polygon) return undefined;

    return {
      ...polygon,
      fillColor: "#ffffff",
      strokeColor: "#64748b",
      text: {
        ...polygon.text,
        show: false,
      },
    };
  }
);

export const selectCeilingAiPredictionPixPolygons = createSelector(
  [selectHttpSingleRoomFull],
  (room): IPixPolygon[] => {
    return (room?.mlPredictedDevices ?? [])
      .map(createAiPredictionPolygon)
      .filter((polygon): polygon is IPixPolygon => polygon != null);
  }
);

export const selectCeilingAiPredictionViewerPixPolygons = createSelector(
  [selectCeilingAiPredictionsRoomPixPolygon, selectCeilingAiPredictionPixPolygons],
  (roomPolygon, predictionPolygons): IPixPolygon[] => {
    return [...(roomPolygon ? [roomPolygon] : []), ...predictionPolygons];
  }
);

export const selectCeilingAiPredictionCeilingGridPixPaths = createSelector(
  [selectActivePartialMcbimSpace],
  (mcbimSpace): IPixPath[] => {
    if (!mcbimSpace) return [];
    return buildCeilingGridPixPathsFromLines([mcbimSpace]);
  }
);

export const selectCeilingAiPredictionExtentKey = createSelector(
  [
    selectHttpSingleRoomFull,
    selectCeilingAiPredictionViewerPixPolygons,
    selectCeilingAiPredictionCeilingGridPixPaths,
  ],
  (room, polygons, paths): string => {
    return [
      "ai-ceiling",
      room?.roomNumber ?? "no-room",
      polygons.length,
      paths.length,
    ].join(":");
  }
);
